import { useQuery } from '@apollo/client';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import AlphaplanRow from './AlphaplanRow.tsx';
import { GET_ALPHAPLAN_INTEGRATIONS } from './queries.ts';
import type { AlphaplanIntegration } from './types.ts';
import { withIcon } from '../../../core/components/Icon.tsx';
import Layout from '../../../core/components/Layout.tsx';
import Page from '../../../core/components/Page/index.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import Table from '../../../core/components/Table.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import { url } from '../../../core/utils/link.ts';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';
import { CUSTOMER_INTEGRATION_ERP_PATH, CUSTOMER_INTEGRATION_ALPHAPLAN_ADD_PATH } from '../../constants.ts';

const AddIcon = withIcon(faPlus);

const AlphaplanIntegrationPage = () => {
    const { t } = useTranslation('integrations');

    const { data, loading, refetch } = useQuery(GET_ALPHAPLAN_INTEGRATIONS, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
    });
    const integrations: AlphaplanIntegration[] = data?.alphaplanIntegrations || [];

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <IntegrationBreadcrumb
                        backTooltip={`${t('backTooltipText')} ${t('erp.pageTitle')}`}
                        items={[
                            { label: t('erp.pageTitle'), to: url(CUSTOMER_INTEGRATION_ERP_PATH) },
                            { label: t('alphaplan.title') },
                        ]}
                    />

                    <div className="flex gap-2">
                        <Page.HeaderButton asChild>
                            <Link to={url(CUSTOMER_INTEGRATION_ALPHAPLAN_ADD_PATH)}>
                                <AddIcon />
                                {t('alphaplan.addButton')}
                            </Link>
                        </Page.HeaderButton>
                    </div>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6 relative">
                    <Table scrollable className={classnames('w-full table-auto', loading && 'opacity-50')}>
                        <Table.Head>
                            <Table.Row>
                                <Table.HeadCell className="w-1/3">{t('alphaplan.table.name')}</Table.HeadCell>
                                <Table.HeadCell className="w-1/3">{t('alphaplan.table.channels')}</Table.HeadCell>
                                <Table.HeadCell>{t('alphaplan.table.masterDataUpdatedAt')}</Table.HeadCell>
                                <Table.HeadCell sticky="right" className="min-w-40 w-40" />
                            </Table.Row>
                        </Table.Head>
                        <Table.Body>
                            {integrations.length > 0 ? (
                                integrations.map((integration) => (
                                    <AlphaplanRow key={integration.id} integration={integration} refetch={refetch} />
                                ))
                            ) : (
                                <Table.Row>
                                    <Table.Cell colSpan={4} className="text-center text-content-medium">
                                        {t('alphaplan.noIntegrations')}
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table>
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default AlphaplanIntegrationPage;
